import {Icon, IconButton, Input, Spinner, Flex, InputGroup} from "@chakra-ui/react";
import {MagnifyingGlassIcon} from "@heroicons/react/24/outline";
import {forwardRef, useCallback, useImperativeHandle, useRef, useState} from "react";
import {useDebouncedEffect} from "../hooks/debouncedEffect";
import {XMarkIcon} from "@heroicons/react/20/solid";
import * as React from "react";
import useTranslate from "platformed-components/i18n/useTranslate";

type Props = {
  query: string;
  onChange: (query: string) => void;
  placeholder?: string;
  debounceTime?: number;
  pending?: boolean;
  disabled?: boolean;
  inputProps?: React.ComponentProps<typeof Input>;
};

const Search = forwardRef<HTMLInputElement, Props>(function Search(
  {query, onChange, placeholder, debounceTime = 0, pending = false, disabled = false, inputProps = {}}: Props,
  ref,
) {
  const t = useTranslate();
  const resolvedPlaceholder = placeholder ?? t("c.Search[ellipsis]");
  // If the query is debounced, we need to store the intermediate value
  // internally.
  const [innerQuery, setInnerQuery] = useState(query);
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => innerRef.current!);

  const isDebounced = debounceTime > 0;
  const showSpinner = pending || (isDebounced && innerQuery !== query);

  useDebouncedEffect(
    () => {
      if (isDebounced) {
        onChange(innerQuery);
      }
    },
    [innerQuery, isDebounced],
    debounceTime,
  );

  const innerOnChange = useCallback(
    (query: string) => {
      if (isDebounced) {
        // If we're debounced, we should set the inner state. The
        // `useDebouncedEffect` will handle calling `onChange` after the
        // debounce period.
        setInnerQuery(query);
      } else {
        // If not, we can notify the parent immediately.
        onChange(query);
      }
    },
    [isDebounced, onChange],
  );

  const value = isDebounced ? innerQuery : query;

  const endElement =
    showSpinner || value.length > 0 ? (
      <Flex align="center" gap={3} pr={1.5}>
        {showSpinner && <Spinner size="sm" color="blue.500" />}
        {value.length > 0 && (
          <IconButton
            size="xs"
            onClick={() => {
              innerOnChange("");
              innerRef.current?.focus();
            }}
            disabled={disabled}
            aria-label={t("c.Clear")}
            variant="ghost"
          >
            <Icon as={XMarkIcon} />
          </IconButton>
        )}
      </Flex>
    ) : undefined;

  return (
    <InputGroup startElement={<Icon as={MagnifyingGlassIcon} h={4} w={4} color="gray.500" />} endElement={endElement}>
      <Input
        placeholder={resolvedPlaceholder}
        value={value}
        onChange={ev => innerOnChange(ev.target.value)}
        ref={innerRef}
        disabled={disabled}
        {...inputProps}
      />
    </InputGroup>
  );
});

export default Search;
